import { z } from "zod";
import { COLLECTION_TITLE_MAX_LENGTH } from "./collection-lifecycle.js";
import {
  AMOUNT_INVALID_MESSAGE,
  AMOUNT_MAX_DIGITS,
  AMOUNT_REQUIRED_MESSAGE,
  INTEGER_STRING_PATTERN,
  PG_BIGINT_MAX,
} from "./collection-schedules.js";
import { adhocDueDateFieldSchema } from "./collections.js";

/**
 * Body các thao tác LÀM HỘ đợt thu của nhân viên admin (US-AE4.F7-1002 · @UC9636).
 *
 * Nhân viên làm thay người quản lý nhóm khi họ nhờ (QĐ 30/09/2026). Mọi thao tác **bắt buộc `reason`**
 * (≤500): sáu tháng sau câu hỏi *"ai làm, vì sao"* phải trả lời được từ `audit_log`, không từ trí nhớ.
 *
 * 🔴 KHÔNG có trường tiền nào ở đây, và `.strict()` chặn field lạ (kể cả `amountMinor`) ở biên — đổi số
 * tiền / hạn là task riêng (Task 3), đi qua contract riêng.
 */
export const ADMIN_COLLECTION_ACTION_REASON_MAX_LENGTH = 500;

/**
 * 409 khi sửa tên / xoá một đợt (kỳ) ĐÃ ĐÓNG — QĐ 30/09: đã đóng thì chỉ được "mở lại", không sửa gì nữa.
 * (Các mã chặn theo nhóm/quỹ — GROUP_ARCHIVED · GROUP_SUSPENDED · FUND_FROZEN — dùng lại mã có sẵn của app.)
 */
export const COLLECTION_CLOSED_READONLY_CODE = "COLLECTION_CLOSED_READONLY" as const;

const reasonField = z.string().trim().min(1).max(ADMIN_COLLECTION_ACTION_REASON_MAX_LENGTH);

/** Đóng · mở lại · xoá đợt thu tạo nhầm — chỉ cần lý do. */
export const adminCollectionReasonBodySchema = z.object({ reason: reasonField }).strict();
export type AdminCollectionReasonBody = z.infer<typeof adminCollectionReasonBodySchema>;

/** Sửa TÊN đợt thu (không mô tả, không tiền). Đợt định kỳ ⇒ áp cả lịch lẫn kỳ đang mở (luật của app). */
export const adminCollectionRenameBodySchema = z
  .object({
    title: z.string().trim().min(1).max(COLLECTION_TITLE_MAX_LENGTH),
    reason: reasonField,
  })
  .strict();
export type AdminCollectionRenameBody = z.infer<typeof adminCollectionRenameBodySchema>;

/**
 * Bật lại lịch thu. `startFrom` giống app: `next` (mặc định) chỉ bật lịch; `current` đảm bảo kỳ đang chứa
 * hôm nay tồn tại và mở.
 */
export const adminResumeScheduleBodySchema = z
  .object({
    reason: reasonField,
    startFrom: z.enum(["current", "next"]).optional(),
  })
  .strict();
export type AdminResumeScheduleBody = z.infer<typeof adminResumeScheduleBodySchema>;

export const adminResumeScheduleResponseSchema = z
  .object({
    scheduleId: z.string().uuid(),
    scheduleStatus: z.literal("active"),
    /** `true` ⇒ lịch đã `active` từ trước, không đổi gì (bấm lại vô hại, không 409). */
    alreadyActive: z.boolean(),
  })
  .strict();
export type AdminResumeScheduleResponse = z.infer<typeof adminResumeScheduleResponseSchema>;

/**
 * ĐỔI SỐ TIỀN đợt thu (US-AE4.F7-1003 · @UC9636). Chỉ hai trường: số mới + lý do. `.strict()` ⇒ KHÔNG nhận mốc kỳ,
 * chế độ chia, hay bất kỳ field nào khác — máy tự quyết đổi từ đâu (đợt một lần: cả đợt khi chưa ai nộp; đợt định
 * kỳ: từ kỳ kế sau kỳ đã sinh), nên client không có cách ép sửa một kỳ đã sinh.
 * `amountMinor` là STRING số nguyên dương ≤ BIGINT (tiền không bao giờ là float / number trên dây).
 */
export const adminCollectionChangeAmountBodySchema = z
  .object({
    amountMinor: z
      .string()
      .trim()
      .min(1, AMOUNT_REQUIRED_MESSAGE)
      .max(AMOUNT_MAX_DIGITS, AMOUNT_INVALID_MESSAGE)
      .regex(INTEGER_STRING_PATTERN, AMOUNT_INVALID_MESSAGE)
      .refine((v) => BigInt(v) > 0n, AMOUNT_REQUIRED_MESSAGE)
      .refine((v) => BigInt(v) <= PG_BIGINT_MAX, AMOUNT_INVALID_MESSAGE),
    reason: reasonField,
  })
  .strict();
export type AdminCollectionChangeAmountBody = z.infer<typeof adminCollectionChangeAmountBodySchema>;

/** Đổi số tiền: áp cho CHÍNH đợt này (đợt một lần) hay TỪ một kỳ kế sau (đợt định kỳ). */
export const ADMIN_AMOUNT_CHANGE_SCOPES = ["this_collection", "from_period"] as const;

export const adminCollectionChangeAmountResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    scope: z.enum(ADMIN_AMOUNT_CHANGE_SCOPES),
    /** Kỳ bắt đầu dùng số mới (chỉ với `from_period`); `null` với `this_collection`. */
    effectiveFromPeriodKey: z.string().nullable(),
    /** Số mới (minor, string). `null` khi vai không được xem tiền (`MONEY_VISIBLE_ROLES`) — cùng luật redact mọi DTO admin. */
    amountMinor: z.string().nullable(),
  })
  .strict();
export type AdminCollectionChangeAmountResponse = z.infer<typeof adminCollectionChangeAmountResponseSchema>;

/** 409 — đợt một lần đã có người chạm tiền (đã đóng / đóng dở / nộp hộ / chờ xác nhận / có lời khai): không đổi số. */
export const COLLECTION_AMOUNT_HAS_PAYMENTS_CODE = "COLLECTION_AMOUNT_HAS_PAYMENTS" as const;
/** 422 — chỉ hỗ trợ chia ĐỀU; chế độ chia khác khoá (làm sau). */
export const COLLECTION_AMOUNT_MODE_LOCKED_CODE = "COLLECTION_AMOUNT_MODE_LOCKED" as const;
/** 422 — số mới bằng số đang có hiệu lực: không ghi gì. */
export const COLLECTION_AMOUNT_UNCHANGED_CODE = "COLLECTION_AMOUNT_UNCHANGED" as const;
/** 409 — lịch đã huỷ hẳn: không còn kỳ nào để áp số mới. */
export const COLLECTION_SCHEDULE_CANCELLED_CODE = "COLLECTION_SCHEDULE_CANCELLED" as const;
/** 422 — kỳ đã sinh gần nhất thiếu ngày kết thúc (dữ liệu cũ) ⇒ không xác định được kỳ kế: fail-closed, không đoán. */
export const COLLECTION_PERIOD_END_MISSING_CODE = "COLLECTION_PERIOD_END_MISSING" as const;
/** 422 — loại đợt không phải "một lần" / "định kỳ" (vd đợt tất toán): số phải đóng do luật khác quyết, không đổi tay ở đây. */
export const COLLECTION_AMOUNT_KIND_LOCKED_CODE = "COLLECTION_AMOUNT_KIND_LOCKED" as const;

/**
 * SỬA HẠN NỘP đợt thu một lần (US-AE4.F7-1005 · @UC9636 · QĐ người dùng 02/10/2026). Hai trường: hạn mới + lý do.
 * Được kéo dài lẫn rút ngắn; "không về quá khứ", "chỉ đợt adhoc", "chưa quá hạn" kiểm ở service (giờ VN, ân hạn của quỹ).
 * `.strict()` ⇒ không nhận trường nào khác (kể cả tiền).
 */
export const adminCollectionChangeDueDateBodySchema = z
  .object({ dueDate: adhocDueDateFieldSchema, reason: reasonField })
  .strict();
export type AdminCollectionChangeDueDateBody = z.infer<typeof adminCollectionChangeDueDateBodySchema>;

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * Kết quả sửa hạn. `remindSuggested` = hạn bị RÚT NGẮN và hạn CŨ đã còn ít hơn `reminder_days_before` ngày ⇒ lời nhắc tự
 * động có thể đã gửi kèm ngày cũ (máy chỉ nhắc một lần mỗi khoản) — màn gợi ý nhờ chủ nhóm bấm Nhắc, kèm nút nhắc của nhân
 * viên. Hạn cũ còn xa ⇒ máy chưa nhắc ai và sẽ tự nhắc theo hạn mới ⇒ `false`.
 */
export const adminCollectionChangeDueDateResponseSchema = z
  .object({
    collectionId: z.string().uuid(),
    dueDateBefore: ymd,
    dueDateAfter: ymd,
    remindSuggested: z.boolean(),
  })
  .strict();
export type AdminCollectionChangeDueDateResponse = z.infer<typeof adminCollectionChangeDueDateResponseSchema>;

/** Hạn mới trùng hạn đang có ⇒ 422 (không ghi audit rỗng). */
export const COLLECTION_DUE_DATE_UNCHANGED_CODE = "COLLECTION_DUE_DATE_UNCHANGED" as const;
/** Không phải đợt một lần (`adhoc`) ⇒ 422. */
export const COLLECTION_DUE_DATE_KIND_LOCKED_CODE = "COLLECTION_DUE_DATE_KIND_LOCKED" as const;
/** Đợt không có hạn ⇒ 422 (đặt hạn mới cho đợt trống không thuộc phạm vi). */
export const COLLECTION_DUE_DATE_MISSING_CODE = "COLLECTION_DUE_DATE_MISSING" as const;
/** Đợt đã quá hạn (hạn + ân hạn của quỹ) ⇒ 409: nhãn "quá hạn" không gỡ được nên không cho dời hạn. */
export const COLLECTION_ALREADY_OVERDUE_CODE = "COLLECTION_ALREADY_OVERDUE" as const;
